(() => {
  if (window.GibbonBoardSwatches) return;
  const products = new Map();
  const primaryImages = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '[data-board-product-link], [data-board-model-url]';
  const main = () => document.getElementById('MainContent');
  const entryUrl = location.href;
  let renderedUrl = entryUrl;
  let entryOrder;
  let refreshing = false;
  let sharedStyles = [];
  const railTransitions = new WeakMap();
  let switchRevision = 0;
  let headingSizes;
  let headingObserver;
  let measuredHeadingWidth = 0;

  function productUrl(value) {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid board URL');
    for (const name of ['preview_theme_id', '_fd', 'pb', 'view']) {
      const current = new URL(entryUrl).searchParams.get(name);
      if (current && !url.searchParams.has(name)) url.searchParams.set(name, current);
    }
    url.searchParams.delete('variant');
    return url;
  }

  function preserveEntryOrder(scope) {
    const rail = scope?.querySelector('[data-board-swatch-rail]');
    if (!rail) return;
    const patternOf = item => item.dataset.boardSwatchItem || item.querySelector('[data-board-pattern]')?.dataset.boardPattern;
    const items = [...rail.children];
    if (!entryOrder) entryOrder = items.map(patternOf).filter(Boolean);
    const byPattern = new Map(items.map(item => [patternOf(item), item]));
    for (const pattern of entryOrder) {
      const item = byPattern.get(pattern);
      if (item) rail.append(item);
      byPattern.delete(pattern);
    }
    for (const item of byPattern.values()) rail.append(item);
  }

  function copyAttributes(target, source, { keep = [] } = {}) {
    for (const attribute of [...target.attributes]) {
      if (!keep.includes(attribute.name) && !source.hasAttribute(attribute.name)) target.removeAttribute(attribute.name);
    }
    for (const attribute of [...source.attributes]) {
      if (!keep.includes(attribute.name)) target.setAttribute(attribute.name, attribute.value);
    }
  }

  // Templates stay inert. Only the chosen product's initializers run after its data is installed.
  function makeScriptsInert(scope) {
    for (const script of scope.querySelectorAll('script')) {
      if (!script.type || ['text/javascript', 'application/javascript'].includes(script.type)) {
        script.type = 'application/x-gibbon-board-script';
      }
    }
  }

  function normalizePrimaryImages(scope) {
    for (const panel of scope.querySelectorAll('[data-sb-top-panel]')) {
      const source = panel.querySelector('.pdp-v2-gallery__main img');
      if (!source?.getAttribute('src')) continue;
      const url = source.getAttribute('src');
      preloadImage(url);
      const gallery = scope.querySelector(`[data-sb-mobile-gallery="${panel.dataset.sbTopPanel}"]`);
      const image = gallery?.querySelector('.product-main-slide img.photoswipe__image');
      if (!image) continue;
      image.setAttribute('src', url);
      for (const name of ['srcset', 'data-src', 'data-srcset', 'data-widths', 'data-sizes']) image.removeAttribute(name);
      image.classList.remove('lazyload', 'lazyloading');
      image.classList.add('lazyloaded');
      image.loading = 'eager';
      image.decoding = 'async';
    }
  }

  function preloadImage(value) {
    if (!value) return;
    const url = new URL(value, location.href).href;
    if (primaryImages.has(url)) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    primaryImages.set(url, image);
    image.src = url;
    // Decode opportunistically; selection never awaits this promise or a network response.
    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }

  function cacheProducts() {
    const host = main()?.querySelector('[data-section-type="product-template"]')?.closest('.shopify-section');
    if (!host?.querySelector('[data-board-swatches]')) return;
    preserveEntryOrder(host);
    if (!sharedStyles.length) sharedStyles = [...host.querySelectorAll('style')].map(style => style.cloneNode(true));
    normalizePrimaryImages(host);
    for (const template of document.querySelectorAll('template[data-board-product-template]')) {
      const url = productUrl(template.dataset.productUrl);
      const content = template.content;
      makeScriptsInert(content);
      content.querySelectorAll('style').forEach(style => style.remove());
      normalizePrimaryImages(content);
      preserveEntryOrder(content);
      products.set(url.pathname, { content, title: template.dataset.productTitle, handle: template.dataset.productHandle });
      preloadImage(template.dataset.primaryImage);
    }
    // Retain the exact entry variant, markup and localized browser title for returning to it.
    const entry = document.createDocumentFragment();
    for (const node of host.childNodes) {
      if (node.nodeType === 1 && node.hasAttribute('data-board-product-cache')) continue;
      entry.append(node.cloneNode(true));
    }
    makeScriptsInert(entry);
    entry.querySelectorAll('style').forEach(style => style.remove());
    const entryPath = new URL(entryUrl).pathname;
    const companion = host.querySelector('[data-board-entry-companion-state]');
    if (companion) {
      const state = companion.cloneNode(true);
      state.setAttribute('data-board-companion-state', '');
      entry.append(state);
    }
    products.set(entryPath, { content: entry, title: document.title });
    host.querySelector('[data-section-type="product-template"]').classList.add('gibbon-board-instant');
  }

  function holdRailPosition(root) {
    const rail = root.querySelector('[data-board-swatch-rail]');
    if (!rail) return () => {};
    const revision = ++switchRevision;
    const left = rail.scrollLeft;
    let original = railTransitions.get(rail);
    if (!original) {
      original = ['scroll-snap-type', 'scroll-behavior'].map(name => ({ name, value: rail.style.getPropertyValue(name), priority: rail.style.getPropertyPriority(name) }));
      railTransitions.set(rail, original);
    }
    rail.style.setProperty('scroll-snap-type', 'none', 'important');
    rail.style.setProperty('scroll-behavior', 'auto', 'important');
    const restore = () => { if (rail.isConnected && revision === switchRevision) rail.scrollLeft = left; };
    return (scheduleFrame = true) => {
      restore();
      if (!scheduleFrame) return;
      requestAnimationFrame(() => {
        if (revision !== switchRevision || !rail.isConnected) return;
        restore();
        for (const property of original) {
          if (property.value) rail.style.setProperty(property.name, property.value, property.priority);
          else rail.style.removeProperty(property.name);
        }
        railTransitions.delete(rail);
        syncRail();
      });
    };
  }

  function applyHeadingSizes(root) {
    if (!root || !headingSizes) return;
    root.style.setProperty('--board-title-height', headingSizes.title + 'px');
    root.style.setProperty('--board-review-height', headingSizes.review + 'px');
  }

  function measureHeadingSizes() {
    const root = main()?.querySelector('[data-section-type="product-template"]');
    const panel = root?.querySelector('.productRight');
    const liveBadge = panel?.querySelector('[data-sb-heading]:not([hidden]) .jdgm-preview-badge');
    if (!root || !panel || !liveBadge || !products.size) return;
    const width = panel.getBoundingClientRect().width;
    if (!width) return;
    const probe = document.createElement('div');
    probe.className = root.className;
    Object.assign(probe.style, { position: 'fixed', left: '-10000px', top: '0', width: width + 'px', visibility: 'hidden', pointerEvents: 'none' });
    probe.setAttribute('aria-hidden', 'true');
    probe.inert = true;
    const probePanel = document.createElement('div');
    probePanel.className = 'productRight';
    Object.assign(probePanel.style, { width: '100%', padding: '0', minWidth: '0' });
    probe.append(probePanel);
    document.body.append(probe);
    const titles = new Map();
    const reviews = new Map();
    const liveInner = liveBadge.querySelector('.jdgm-prev-badge');
    for (const product of products.values()) {
      for (const heading of product.content.querySelectorAll('[data-sb-heading]')) {
        const title = heading.querySelector('h1.productTitle');
        if (title) titles.set(title.textContent, title);
        const badge = heading.querySelector('.jdgm-prev-badge');
        if (badge) reviews.set(badge.dataset.averageRating + ':' + badge.dataset.numberOfReviews, badge);
      }
    }
    let titleHeight = 0;
    let reviewHeight = 24;
    try {
      for (const source of titles.values()) {
        const title = source.cloneNode(true);
        title.style.setProperty('min-height', '0', 'important');
        title.style.height = 'auto';
        probePanel.replaceChildren(title);
        titleHeight = Math.max(titleHeight, Math.ceil(title.getBoundingClientRect().height));
      }
      for (const source of reviews.values()) {
        const badge = liveBadge.cloneNode(true);
        badge.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        badge.removeAttribute('id');
        badge.style.setProperty('min-height', '0', 'important');
        badge.style.setProperty('display', 'block', 'important');
        badge.style.height = 'auto';
        const inner = badge.querySelector('.jdgm-prev-badge');
        if (inner) {
          const display = liveInner && getComputedStyle(liveInner).display;
          inner.style.setProperty('display', display && display !== 'none' ? display : 'block', 'important');
        }
        const rating = badge.querySelector('.jdgm-rating-bold');
        if (rating && source.dataset.averageRating) rating.textContent = source.dataset.averageRating;
        const count = badge.querySelector('.jdgm-prev-badge__text2');
        if (count && liveInner?.dataset.numberOfReviews && source.dataset.numberOfReviews) {
          count.textContent = count.textContent.replace(liveInner.dataset.numberOfReviews, source.dataset.numberOfReviews);
        }
        probePanel.replaceChildren(badge);
        reviewHeight = Math.max(reviewHeight, Math.ceil(badge.getBoundingClientRect().height));
      }
    } finally { probe.remove(); }
    headingSizes = { title: titleHeight, review: reviewHeight };
    measuredHeadingWidth = width;
    applyHeadingSizes(root);
  }

  function watchHeadingSizes() {
    const panel = main()?.querySelector('.productRight');
    if (panel && window.ResizeObserver && !headingObserver) {
      headingObserver = new ResizeObserver(() => {
        if (Math.abs(panel.getBoundingClientRect().width - measuredHeadingWidth) > 1) measureHeadingSizes();
      });
      headingObserver.observe(panel);
    }
    document.fonts?.ready?.then(measureHeadingSizes);
    document.fonts?.addEventListener?.('loadingdone', measureHeadingSizes);
  }

  function preserveSwatches(current, incoming) {
    const existing = current.querySelector('[data-board-swatches]');
    const next = incoming.querySelector('[data-board-swatches]');
    if (!existing || !next) return;
    copyAttributes(existing, next);
    existing.querySelector('.gibbon-board-swatches__header p').innerHTML = next.querySelector('.gibbon-board-swatches__header p').innerHTML;
    const rail = existing.querySelector('[data-board-swatch-rail]');
    for (const item of rail.children) {
      const replacement = next.querySelector(`[data-board-swatch-item="${item.dataset.boardSwatchItem}"]`);
      // A pattern the market only sells in the other model is rendered hidden; follow the incoming model's state.
      if (!replacement) { item.hidden = true; continue; }
      item.hidden = replacement.hidden;
      const link = item.querySelector('.gibbon-board-swatches__choice');
      const nextLink = replacement.querySelector('.gibbon-board-swatches__choice');
      if (link && nextLink && link.tagName === nextLink.tagName) copyAttributes(link, nextLink);
      else if (link && nextLink) link.replaceWith(nextLink.cloneNode(true));
      const badge = item.querySelector('.gibbon-board-swatches__deal-slot');
      badge.replaceChildren(...replacement.querySelector('.gibbon-board-swatches__deal-slot').cloneNode(true).childNodes);
    }
    const status = existing.querySelector('[data-board-switch-status]');
    status.textContent = '';
    status.classList.add('visually-hidden');
    next.replaceWith(existing);
  }

  function preserveModels(current, incoming) {
    for (const card of current.querySelectorAll('[data-board-model-url]')) {
      const next = incoming.querySelector(`[data-sb-switch="${card.dataset.sbSwitch}"][data-board-model-url]`);
      if (!next) continue;
      copyAttributes(card, next);
      card.replaceChildren(...next.childNodes);
      next.replaceWith(card);
    }
  }

  function preserveForm(current, incoming) {
    const form = current.querySelector('form.product-single__form');
    const next = incoming.querySelector('form.product-single__form');
    if (!form || !next) return;
    const button = form.querySelector('[data-add-to-cart]');
    const nextButton = next.querySelector('[data-add-to-cart]');
    if (button && nextButton) {
      copyAttributes(button, nextButton);
      button.replaceChildren(...nextButton.childNodes);
      button.disabled = nextButton.disabled;
      nextButton.replaceWith(button);
    }
    copyAttributes(form, next);
    form.replaceChildren(...next.childNodes);
    next.replaceWith(form);
  }

  function preserveShell(current, incoming) {
    preserveSwatches(current, incoming);
    preserveModels(current, incoming);
    preserveForm(current, incoming);
    for (const selector of ['.stickyProduct', '.productLeft', '.productRight', '.productGrid']) {
      const existing = current.querySelector(selector);
      const next = incoming.querySelector(selector);
      if (!existing || !next) continue;
      copyAttributes(existing, next);
      existing.replaceChildren(...next.childNodes);
      next.replaceWith(existing);
    }
    copyAttributes(current, incoming);
    current.replaceChildren(...incoming.childNodes);
    current.classList.add('gibbon-board-instant');
    applyHeadingSizes(current);
    incoming.replaceWith(current);
  }

  function sectionEvent(host, type) {
    const root = host.querySelector('[data-section-id]');
    if (root) host.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { sectionId: root.dataset.sectionId } }));
  }

  function refreshScripts(scope) {
    for (const old of scope.querySelectorAll('script[type="application/x-gibbon-board-script"]:not([src])')) {
      const script = document.createElement('script');
      script.textContent = '(function(){\n' + old.textContent.replace(
        /document\.addEventListener\(\s*(['"])DOMContentLoaded\1/g,
        'window.GibbonBoardLifecycle.readyEvent("DOMContentLoaded"'
      ) + '\n})();';
      old.replaceWith(script);
    }
    for (const old of scope.querySelectorAll('script[src^="https://static.truemed.com/widgets/product-page-widget"]')) {
      const script = document.createElement('script');
      script.src = old.src;
      script.defer = true;
      old.replaceWith(script);
    }
  }

  function updateCompanions(content) {
    const source = content.querySelector('[data-board-companion-state]');
    if (!source) return;
    let state;
    try { state = JSON.parse(source.textContent); } catch (_) { return; }
    const quiz = main()?.querySelector('.quizEntry');
    if (quiz) {
      quiz.style.setProperty('display', state.quizEnabled ? 'block' : 'none', 'important');
      if (state.quizBackground) {
        quiz.setAttribute('data-board-quiz-background', '');
        quiz.style.setProperty('--board-quiz-background', state.quizBackground);
      }
      for (const [selector, src] of [['.qeCol1 .qeImageDesktop', state.quizOriginalImage], ['.qeCol3 .qeImageDesktop', state.quizPlusImage]]) {
        const image = quiz.querySelector(selector);
        if (image && src) { image.src = src; image.removeAttribute('srcset'); }
      }
    }
    main()?.querySelectorAll('.newSlider').forEach(slider => {
      slider.style.setProperty('display', state.quizEnabled ? 'block' : 'none', 'important');
      const carousel = slider.querySelector('.slider.slick-initialized');
      if (state.quizEnabled && carousel && window.jQuery) window.jQuery(carousel).slick('setPosition');
    });
    if (state.faqBackground) main()?.querySelectorAll('[id^="FAQSection-"]').forEach(faq => { faq.style.backgroundColor = state.faqBackground; });
    const widget = main()?.querySelector('.jdgm-review-widget[data-entry-point]');
    if (widget) {
      widget.dataset.id = String(state.reviewProductId);
      widget.dataset.productId = String(state.reviewProductId);
      widget.dataset.productTitle = state.reviewProductTitle || '';
      widget.dataset.productUrl = state.reviewProductUrl || '';
      widget.dataset.url = state.reviewProductUrl || '';
      const reviewSection = widget.closest('.shopify-section');
      if (reviewSection) reviewSection.style.setProperty('display', state.reviewsHidden ? 'none' : 'block', 'important');
    }
  }

  function syncRail(root = main()) {
    root?.querySelectorAll('[data-board-weekly-deal-end]').forEach(badge => {
      if (Number(badge.dataset.boardWeeklyDealEnd) * 1000 <= Date.now()) badge.hidden = true;
    });
    root?.querySelectorAll('[data-board-swatches]').forEach(swatches => {
      const rail = swatches.querySelector('[data-board-swatch-rail]');
      const max = rail.scrollWidth - rail.clientWidth;
      const previous = swatches.querySelector('[data-board-swatch-scroll="-1"]');
      const next = swatches.querySelector('[data-board-swatch-scroll="1"]');
      if (previous) previous.disabled = rail.scrollLeft < 2;
      if (next) next.disabled = rail.scrollLeft > max - 2;
      if (!rail.dataset.bound) {
        rail.dataset.bound = 'true';
        rail.addEventListener('scroll', () => syncRail(), { passive: true });
      }
    });
  }

  function switchProduct(value, { link = null } = {}) {
    if (refreshing) return false;
    let url;
    try { url = productUrl(value); } catch (_) { return false; }
    if (url.pathname === new URL(renderedUrl).pathname) return true;
    if (!products.size) cacheProducts();
    const source = products.get(url.pathname);
    const current = main()?.querySelector('[data-section-type="product-template"]');
    const host = current?.closest('.shopify-section');
    if (!source || !current || !host) return false;
    const content = source.content.cloneNode(true);
    const incoming = content.querySelector('[data-section-type="product-template"]');
    if (!incoming?.querySelector('[data-board-swatches]')) return false;
    const companion = content.querySelector('[data-board-companion-state]')?.cloneNode(true);
    const persistent = host.querySelector('[data-board-product-cache]');
    const restoreRail = holdRailPosition(current);
    refreshing = true;
    try {
      sectionEvent(host, 'shopify:section:unload');
      // Paired mobile carousels are custom instances, so explicitly dispose them before moving their DOM.
      current.querySelectorAll('[data-product-photos]').forEach(slider => window.Flickity?.data(slider)?.destroy());
      window.GibbonBoardLifecycle?.reset();
      preserveShell(current, incoming);
      host.replaceChildren(...content.childNodes);
      host.prepend(...sharedStyles.map(style => style.cloneNode(true)));
      if (persistent) host.append(persistent);
      restoreRail();
      sectionEvent(host, 'shopify:section:load');
      refreshScripts(host);
      // Snapshot markup decides whether the model's video exists; entry-model CSS must not hide it.
      host.querySelectorAll('.pdp_single_video').forEach(video => video.style.setProperty('display', 'block', 'important'));
      window.theme?.collapsibles?.init(host);
      window.GibbonWinnerPages?.init?.(host);
      if (companion) updateCompanions({ querySelector: () => companion });
      for (const id of ['quiz', 'comparison']) {
        main().querySelectorAll(`#${id}`).forEach((node, index) => { if (index > 0) node.remove(); });
      }
      document.title = source.title || document.title;
      renderedUrl = url.href;
      syncRail();
      if (link?.isConnected) link.focus({ preventScroll: true });
      restoreRail(false);
      document.dispatchEvent(new CustomEvent('gibbon:product-switch', { detail: {
        url: renderedUrl, renderedUrl, entryUrl,
        handle: current.querySelector('[data-board-product-link][aria-current="true"]')?.getAttribute('href'),
        productHandle: source.handle || url.pathname.split('/').filter(Boolean).pop(),
        productId: current.dataset.productId,
        variantId: current.querySelector('form.product-single__form [name="id"]')?.value
      } }));
      window.jdgm?.batchRenderBadges?.();
      return true;
    } finally { refreshing = false; }
  }

  function activate(event) {
    const link = event.target.closest(selector);
    if (!link || !main()?.contains(link) || event.target.closest('.compTrigger, .optionPerk u')) return;
    if (event.type === 'click' && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    if (event.type === 'keydown' && (!link.hasAttribute('data-board-model-url') || !['Enter', ' '].includes(event.key))) return;
    const value = link.dataset.boardModelUrl || link.href;
    if (!value) return;
    // Missing catalog entries keep their native product link; there is no on-demand product fetch.
    if (!products.size) cacheProducts();
    let url;
    try { url = productUrl(value); } catch (_) { return; }
    if (!products.has(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    switchProduct(value, { link });
  }

  document.addEventListener('click', activate, true);
  document.addEventListener('keydown', activate, true);
  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-board-swatch-scroll]');
    if (!arrow) return;
    const rail = arrow.closest('[data-board-swatches]').querySelector('[data-board-swatch-rail]');
    rail.scrollBy({ left: Number(arrow.dataset.boardSwatchScroll) * rail.clientWidth * .8, behavior: motion.matches ? 'instant' : 'smooth' });
  });
  window.addEventListener('resize', () => { measureHeadingSizes(); syncRail(); });
  window.setInterval(() => syncRail(), 60000);
  const init = () => { cacheProducts(); measureHeadingSizes(); watchHeadingSizes(); syncRail(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.GibbonBoardSwatches = { switchProduct };
})();
